import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { atCreateLaborRequest, atCheckSession, listWorkforceSkills } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const GREEN = "#1A7A4A";
const RED = "#A32D2D";

/** Contractor creates a labor request. On submit → immediate SMS + email fan-out
 *  to every worker currently 🟢 Available Today whose skills overlap.
 *  Redirects to /available-today/requests/{id} with the live match list. */
export default function AvailableTodayNewRequestPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [skills, setSkills] = useState([]);
  const [form, setForm] = useState({
    skills: [], city: "", state: "", radius_miles: 20,
    workers_needed: 1, hourly_offered_usd: 30, urgency: "asap",
    start_at: "", end_at: "", required_osha: false, required_license: false,
    preferred_language: "", description: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    atCheckSession().then((s) => setSession(s)).catch(() => setSession({ access: false }));
    listWorkforceSkills().then((d) => setSkills(d.items || [])).catch(() => {});
  }, []);

  const toggleSkill = (s) => {
    setForm((f) => f.skills.includes(s)
      ? { ...f, skills: f.skills.filter((x) => x !== s) }
      : { ...f, skills: [...f.skills, s] });
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (form.skills.length === 0) return setError("Pick at least one skill.");
    setBusy(true); setError(null);
    try {
      const payload = { ...form };
      // Strip empty strings so the backend uses defaults
      if (!payload.start_at) delete payload.start_at;
      if (!payload.end_at) delete payload.end_at;
      if (!payload.preferred_language) delete payload.preferred_language;
      if (!payload.description) delete payload.description;
      const r = await atCreateLaborRequest(payload);
      navigate(`/available-today/requests/${r.request.id}`, { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not post request.");
    } finally {
      setBusy(false);
    }
  };

  if (session && !session.access) {
    return <SignInRequired />;
  }

  return (
    <div style={pageWrap} data-testid="new-request-page">
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>AVAILABLE TODAY · POST A LABOR REQUEST</div>
        <h1 style={h1}>NEED HELP — WHO, WHEN, WHERE?</h1>
        <p style={{ color: "#5b5e69", marginBottom: 24, fontSize: 15 }}>
          As soon as you hit submit, every 🟢 Available Today worker whose skills match will get an SMS + email with your offer. If the top match hasn&apos;t responded in {5} minutes, ARIA calls them via Retell.
        </p>

        <form onSubmit={submit} style={card}>
          <Section title="SKILLS · pick everything the job needs">
            <div style={pillGrid} data-testid="request-skills-pills">
              {skills.map((s) => (
                <button type="button" key={s.id} onClick={() => toggleSkill(s.name)}
                  data-testid={`req-skill-${s.slug}`}
                  style={{
                    padding: "10px 14px", borderRadius: 22,
                    border: `1.5px solid ${form.skills.includes(s.name) ? NAVY : "#d0d3da"}`,
                    background: form.skills.includes(s.name) ? NAVY : "#fff",
                    color: form.skills.includes(s.name) ? GOLD : NAVY,
                    fontSize: 13, fontWeight: 700, cursor: "pointer",
                  }}>{s.name}</button>
              ))}
            </div>
          </Section>

          <Section title="WHERE + WHEN">
            <Row>
              <Field label="City *"><Input value={form.city} onChange={(v) => setForm({...form, city: v})} testid="req-city" placeholder="Providence" required /></Field>
              <Field label="State"><Input value={form.state} onChange={(v) => setForm({...form, state: v.toUpperCase().slice(0,2)})} testid="req-state" placeholder="RI" style={{maxWidth: 80}} /></Field>
              <Field label="Radius (mi)"><Input type="number" value={form.radius_miles} onChange={(v) => setForm({...form, radius_miles: v})} testid="req-radius" style={{maxWidth: 100}} /></Field>
            </Row>
            <Row>
              <Field label="Urgency">
                <select value={form.urgency} onChange={(e) => setForm({...form, urgency: e.target.value})} style={inputStyle} data-testid="req-urgency">
                  <option value="asap">ASAP · Today</option>
                  <option value="tomorrow">Tomorrow</option>
                  <option value="this_week">This week</option>
                </select>
              </Field>
              <Field label="Start (optional)"><Input type="datetime-local" value={form.start_at} onChange={(v) => setForm({...form, start_at: v})} testid="req-start" /></Field>
              <Field label="End (optional)"><Input type="datetime-local" value={form.end_at} onChange={(v) => setForm({...form, end_at: v})} testid="req-end" /></Field>
            </Row>
          </Section>

          <Section title="CREW + PAY">
            <Row>
              <Field label="Workers needed"><Input type="number" min={1} max={20} value={form.workers_needed} onChange={(v) => setForm({...form, workers_needed: Number(v) || 1})} testid="req-workers" style={{maxWidth: 120}} required /></Field>
              <Field label="Hourly offered ($)"><Input type="number" value={form.hourly_offered_usd} onChange={(v) => setForm({...form, hourly_offered_usd: Number(v) || 0})} testid="req-hourly" style={{maxWidth: 150}} /></Field>
              <Field label="Preferred language"><Input value={form.preferred_language} onChange={(v) => setForm({...form, preferred_language: v})} testid="req-language" placeholder="English" /></Field>
            </Row>
            <Row>
              <label style={checkboxLabel} data-testid="req-osha-label">
                <input type="checkbox" checked={form.required_osha} onChange={(e) => setForm({...form, required_osha: e.target.checked})} data-testid="req-osha" style={{ width: 18, height: 18, accentColor: NAVY }} />
                Require OSHA certified
              </label>
              <label style={checkboxLabel}>
                <input type="checkbox" checked={form.required_license} onChange={(e) => setForm({...form, required_license: e.target.checked})} data-testid="req-license" style={{ width: 18, height: 18, accentColor: NAVY }} />
                Require trade license
              </label>
            </Row>
          </Section>

          <Section title="JOB DESCRIPTION (optional)">
            <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                      data-testid="req-description" rows={4} maxLength={1500}
                      placeholder="Any details a worker should know before accepting — site conditions, scope, gate code, etc."
                      style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
          </Section>

          {error && <div data-testid="new-request-error" style={errBox}>{error}</div>}
          <button type="submit" disabled={busy || form.skills.length === 0} data-testid="new-request-submit"
                  style={{...btnPrimary, opacity: busy ? 0.6 : 1}}>
            {busy ? "Posting…" : "POST REQUEST · NOTIFY WORKERS NOW →"}
          </button>
          <p style={{ marginTop: 14 }}>
            <Link to="/available-today/requests" data-testid="back-to-requests" style={{ color: NAVY, textDecoration: "none", fontWeight: 700, letterSpacing: 1 }}>
              ← My requests
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}

const SignInRequired = () => (
  <div style={pageWrap} data-testid="new-request-signin-required">
    <div style={{ maxWidth: 480, margin: "80px auto", textAlign: "center" }}>
      <h1 style={h1}>SIGN IN TO POST</h1>
      <p style={{ color: "#5b5e69" }}>Posting labor requests is included on the <strong>$149 Platinum (NearNow Autopilot Super)</strong> and <strong>$299 AutoPilot Growth</strong> plans. Sign in with the email on your subscription.</p>
      <Link to="/available-today" style={{ ...btnPrimary, display: "inline-block", textDecoration: "none", marginTop: 20 }}>
        ← BACK TO BROWSE
      </Link>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 22 }}>
    <div style={{ color: NAVY, letterSpacing: 2, fontSize: 12, fontWeight: 700, marginBottom: 10, borderBottom: `2px solid ${GOLD}`, paddingBottom: 6 }}>{title}</div>
    {children}
  </div>
);
const Row = ({ children }) => <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>{children}</div>;
const Field = ({ label, children }) => (
  <div style={{ flex: 1, minWidth: 160 }}>
    <div style={{ color: "#5b5e69", fontSize: 11, letterSpacing: 1.5, fontWeight: 700, marginBottom: 6 }}>{label}</div>
    {children}
  </div>
);
const Input = ({ value, onChange, type="text", testid, placeholder, style, required, min, max }) => (
  <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
         placeholder={placeholder} required={required} min={min} max={max}
         data-testid={testid} style={{ ...inputStyle, ...(style||{}) }} />
);

const pageWrap = { background: "#F8F9FA", minHeight: "100vh", padding: "40px 20px" };
const card = { background: "#fff", borderRadius: 18, padding: "30px 30px", border: "1px solid #e5e7ee", boxShadow: "0 20px 40px -20px rgba(10,22,40,0.12)" };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 44, color: NAVY, margin: "6px 0 14px" };
const inputStyle = { width: "100%", padding: "12px 14px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 15, outline: "none", boxSizing: "border-box", background: "#fff" };
const btnPrimary = { width: "100%", background: GOLD, color: NAVY, border: "none", padding: "18px 24px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 16, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", marginTop: 12 };
const errBox = { color: RED, background: "#FDECEC", padding: "12px 14px", borderRadius: 10, marginBottom: 14, fontSize: 14 };
const pillGrid = { display: "flex", flexWrap: "wrap", gap: 8 };
const checkboxLabel = { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "#F5F7FA", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700, color: NAVY };
